import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router";
import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LoopRing } from "@/components/ui/LoopRing";
import { HeroStage } from "./HeroStage";
import { BOOKING_URL, signupUrl } from "@/lib/links";

/** Who it is for, each linking to that role's solution page. */
const ROLES = [
  { label: "SOC", href: "/solutions/soc" },
  { label: "Threat hunters", href: "/solutions/threat-hunters" },
  { label: "IR", href: "/solutions/incident-response" },
  { label: "MSSPs", href: "/solutions/mssp" },
];

/**
 * Hero copy. The outcome line is the H1 (final, 2026-10-23); the campaign line
 * "Hunt at AI speed." is the eyebrow and never stands alone.
 * Copy source: the story refresh messaging house, 2026-10-22.
 */
const HERO = {
  eyebrow: "Hunt at AI speed.",
  lines: ["Know if you've been hit.", "Same day, with evidence."],
  sub: "Search the security tools you already run, ask any host what was never logged, and let Scout, your investigation partner, do the legwork. Your team approves every step.",
} as const;

/**
 * The homepage hero: a full-bleed dark stage where the Huntbase mark hunts
 * through a field of log rows and lights up the ones that were never logged.
 * Copy first, one visual, three ways forward. No product mock here: the
 * "How" section shows one focused component per step instead.
 */
export function HomeHero() {
  const reduce = useReducedMotion();
  const copy = useRef<HTMLDivElement>(null);
  const v = HERO;
  const rise = (delay: number) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 18 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, delay, ease: [0.2, 0.7, 0.2, 1] as const },
        };

  return (
    <section className="relative bg-background" aria-labelledby="hero-heading">
      <HeroStage
        avoid={copy}
        className="relative isolate overflow-hidden bg-ink text-white pt-page pb-16 md:pb-28 cursor-crosshair"
      >
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 pt-6 md:pt-14">
          <div ref={copy} className="max-w-3xl">
            <motion.p
              {...rise(0.05)}
              className="mb-6 flex items-center gap-3 text-base font-semibold text-indigo-300 md:mb-7 md:text-lg"
            >
              <LoopRing size={16} color="var(--indigo-300)" />
              {v.eyebrow}
            </motion.p>
            <h1
              id="hero-heading"
              /* One sanctioned step above heading-xl: the opening statement of the site. */
              className="heading-xl max-w-[16ch] text-white text-balance lg:text-[5.25rem] lg:leading-[0.98] lg:tracking-[-0.04em]"
            >
              <motion.span {...rise(0.18)} className="block">
                {v.lines[0]}
              </motion.span>
              <motion.span {...rise(0.31)} className="block text-indigo-300">
                {v.lines[1]}
              </motion.span>
            </h1>
            <motion.p
              {...rise(0.46)}
              className="mt-6 max-w-xl text-lg leading-relaxed text-white/75 sm:text-xl md:mt-7"
            >
              {v.sub}
            </motion.p>
            <motion.div
              {...rise(0.6)}
              className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center md:mt-9"
            >
              <a
                href={signupUrl("home-hero")}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Sign up
                <ArrowRight
                  size={18}
                  weight="bold"
                  aria-hidden
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
              <Link
                to="/demo"
                data-cta-placement="home-hero"
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-white/10"
              >
                Watch Scout hunt
                <ArrowRight
                  size={18}
                  weight="bold"
                  aria-hidden
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
              <Link
                to={BOOKING_URL}
                data-cta-placement="home-hero"
                className="inline-flex items-center justify-center px-2 py-2 text-base font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors hover:decoration-white sm:ml-2"
              >
                Book a demo
              </Link>
            </motion.div>
            <motion.nav
              {...rise(0.7)}
              aria-label="Solutions by role"
              className="mt-8 hidden flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/70 sm:flex"
            >
              <span>For</span>
              {ROLES.map((role, i) => (
                <span key={role.href} className="flex items-center gap-2">
                  {i > 0 && (
                    <span aria-hidden className="text-white/60">
                      ·
                    </span>
                  )}
                  <Link
                    to={role.href}
                    className="font-medium text-white/85 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white hover:decoration-white"
                  >
                    {role.label}
                  </Link>
                </span>
              ))}
            </motion.nav>
          </div>
        </div>
      </HeroStage>
    </section>
  );
}
